import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Terminal, Trophy, Target, Zap, BookOpen, Gamepad2, User, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const Dashboard = () => {
  const { user, logout, fetchProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelProgress = () => {
    const xp = stats?.xp || 0;
    if (xp < 2000) return (xp / 2000) * 100;
    if (xp < 5000) return ((xp - 2000) / 3000) * 100;
    return 100;
  };

  const getNextLevelXP = () => {
    const xp = stats?.xp || 0;
    if (xp < 2000) return 2000;
    if (xp < 5000) return 5000;
    return 5000;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-white/10 bg-zinc-900/30 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Terminal className="w-6 h-6 text-primary" />
            <span className="text-xl font-grotesk font-bold text-white">BLOCKCHAIN QUEST</span>
          </div>
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/profile')}
              data-testid="nav-profile-button"
              className="text-zinc-400 hover:text-white"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </Button>
            <Button 
              variant="ghost" 
              onClick={logout}
              data-testid="nav-logout-button"
              className="text-zinc-400 hover:text-primary"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Exit
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Header */}
        <div className="mb-12" data-testid="dashboard-header">
          <span className="text-xs font-mono uppercase tracking-widest text-primary/80">System Dashboard</span>
          <h1 className="text-4xl md:text-5xl font-grotesk font-bold text-white mt-2 mb-4 tracking-tight">
            Welcome back, <span className="text-primary">{user?.username}</span>
          </h1>
          <p className="text-zinc-400">Continue your journey to blockchain mastery</p>
        </div>

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[minmax(180px,auto)] mb-8">
          {/* XP Card */}
          <div className="md:col-span-4 bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="xp-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Experience</span>
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <div className="text-4xl font-grotesk font-bold text-white mb-2">{stats?.xp || 0}</div>
            <div className="text-sm text-zinc-400 mb-3">XP / {getNextLevelXP()} to next level</div>
            <Progress value={getLevelProgress()} className="h-2" data-testid="xp-progress" />
          </div>

          {/* Level Card */}
          <div className="md:col-span-4 bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="level-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Current Level</span>
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div className="text-4xl font-grotesk font-bold text-primary mb-2 uppercase">{stats?.level || 'Beginner'}</div>
            <div className="text-sm text-zinc-400">Keep learning to advance</div>
          </div>

          {/* Score Card */}
          <div className="md:col-span-4 bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="score-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Total Score</span>
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <div className="text-4xl font-grotesk font-bold text-white mb-2">{stats?.total_score || 0}</div>
            <div className="text-sm text-zinc-400">Points earned from games</div>
          </div>

          {/* Chapters Progress */}
          <div className="md:col-span-6 bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="chapters-progress-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Learning Progress</span>
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div className="text-4xl font-grotesk font-bold text-white mb-2">{stats?.completed_chapters || 0}/45</div>
            <div className="text-sm text-zinc-400 mb-3">Chapters completed</div>
            <Progress value={((stats?.completed_chapters || 0) / 45) * 100} className="h-2" />
          </div>

          {/* Games Played */}
          <div className="md:col-span-6 bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="games-played-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Games Played</span>
              <Gamepad2 className="w-5 h-5 text-primary" />
            </div>
            <div className="text-4xl font-grotesk font-bold text-white mb-2">{stats?.total_games || 0}</div>
            <div className="text-sm text-zinc-400">Practice makes perfect</div>
          </div>
        </div>

        {/* Learning Paths */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-grotesk font-semibold text-white mb-6 tracking-tight">Start Learning</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/chapters/beginner')}
              data-testid="level-beginner-button"
              className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 text-left group"
            >
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80 mb-2 block">Level 01</span>
              <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-2">Beginner</h3>
              <p className="text-sm text-zinc-400">15 chapters • Fundamentals</p>
            </button>

            <button
              onClick={() => navigate('/chapters/intermediate')}
              data-testid="level-intermediate-button"
              className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 text-left group"
            >
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80 mb-2 block">Level 02</span>
              <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-2">Intermediate</h3>
              <p className="text-sm text-zinc-400">15 chapters • Smart Contracts & DeFi</p>
            </button>

            <button
              onClick={() => navigate('/chapters/advanced')}
              data-testid="level-advanced-button"
              className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 text-left group"
            >
              <span className="text-xs font-mono uppercase tracking-widest text-primary/80 mb-2 block">Level 03</span>
              <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-2">Advanced</h3>
              <p className="text-sm text-zinc-400">15 chapters • Security & Future</p>
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/games')}
            data-testid="play-games-button"
            className="bg-primary/10 border border-primary/30 rounded-xl p-6 hover:bg-primary/20 hover:shadow-green-glow text-left group"
          >
            <Gamepad2 className="w-8 h-8 text-primary mb-3" />
            <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-1">Play Games</h3>
            <p className="text-sm text-zinc-400">Test your skills in 8 different game modes</p>
          </button>

          <button
            onClick={() => navigate('/leaderboard')}
            data-testid="view-leaderboard-button"
            className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/10 text-left group"
          >
            <Trophy className="w-8 h-8 text-primary mb-3" />
            <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-1">Leaderboard</h3>
            <p className="text-sm text-zinc-400">See how you rank against other learners</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
